
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Grid, Button, Table, TableBody, TableCell, TableHead, TableRow,
    TableContainer, Paper, IconButton, Menu, MenuItem,
    Stack, Chip, Pagination, FormControl, InputLabel, Select,
    Box, Tooltip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import useStyles from "../../assets/styles/SupMangComp.style";
import API from '../../utils/Axios.utils';
import useDebounce from '../../utils/useDebounce';
import CustomModel from '../../utils/CustomModel';
import AdminStatusUpdateForm from './AdminStatusUpdateForm';
import { Notification } from "../../utils/Notification";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useAuth from "../../hooks/useAuth";
import { format } from "date-fns";

const SupplierCandidateList = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { token } = useAuth();
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const location = useLocation();
    const jobCode = location.state?.jobCode;

    // Pagination
    const [listCount, setListCount] = useState(0);
    const [page, setPage] = useState(1);

    // API data
    const [candidates, setCandidates] = useState([]);
    const [customer, setCustomer] = useState();

    // ✅ UPDATED: Use user_id instead of supplier_id
    const [selectedUserIds, setSelectedUserIds] = useState([]); // UI only - user_id
    const [appliedUserIds, setAppliedUserIds] = useState([]);   // API only - user_id

    const [supplierData, setSupplierData] = useState([]);
    const [showLoader, setShowLoader] = useState(false);
    const [searchText, setSearchText] = React.useState('');

    // Notification state
    const [alert, setAlert] = useState(false);
    const [alertContent, setAlertContent] = useState("");
    const [alertType, setAlertType] = useState("");

    // Status Update Modal state
    const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [update, setUpdate] = useState(0);

    // More Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const open = Boolean(anchorEl);

    const handleAlert = () => {
        setAlert(false);
    };

    // ✅ UPDATED: getProfiles uses appliedUserIds with user_id param
    const getProfiles = useCallback(async () => {
        if (!jobCode) {
            console.error('No jobCode found in location.state');
            return;
        }

        try {
            let url = `/api/profile/supplier_candidate/job/${jobCode}?page=${page}&pageSize=10`;

            // ✅ CHANGED: Use user_id parameter (not supplierid)
            if (appliedUserIds.length > 0) {
                url += `&userid=${appliedUserIds.join(',')}`; // ✅ userid param
            }

            console.log('🔍 API URL:', url);
            console.log('🔍 Applied User IDs:', appliedUserIds);

            const res = await API.get(url, config);
            setCandidates(res?.data?.candidates || []);
            setListCount(res?.data?.totalCount || 0);
            setCustomer(res?.data?.customer?.name || "-");
        } catch (error) {
            console.error('Error fetching candidates:', error);
        }
    }, [page, jobCode, appliedUserIds, update]); // ✅ appliedUserIds

    const handlePageChange = (e, value) => {
        setPage(value);
    };

    // Supplier filter handlers
    const debouncedQueryList = useDebounce(searchText, 500);

    const getSupplier = async () => {
        setShowLoader(true);
        let url = 'api/users/getallsupplier';
        if (searchText) url += `?search=${debouncedQueryList}`;

        try {
            const res = await API.get(url, config);
            setShowLoader(false);
            setSupplierData(res?.data?.supplier || []);
        } catch (error) {
            setShowLoader(false);
            console.error('Error fetching suppliers:', error);
        }
    };

    useEffect(() => {
        getSupplier();
    }, [debouncedQueryList]);

    // ✅ UPDATED: handleSupplierChange uses user_id
    const handleSupplierChange = (event) => {
        const selectedValues = event.target.value || [];

        if (selectedValues.includes('all') || selectedValues.some(val => val === 'all')) {
            // ✅ CHANGED: Use user_id instead of supplier_id
            const allUserIds = supplierData.map(supplier => supplier.user_id);
            setSelectedUserIds(allUserIds.map(id => id.toString()));
        } else {
            setSelectedUserIds(selectedValues);
        }
    };

    const handleSubmit = () => {
        setAppliedUserIds(selectedUserIds); // UI → Applied
        setPage(1);
        console.log('✅ Applied user_ids:', selectedUserIds);
    };

    const handleClearFilters = () => {
        setSelectedUserIds([]);     // UI state
        setAppliedUserIds([]);      // API state
        setPage(1);
        console.log('🧹 Cleared all filters');
    };

    // More Menu handlers
    const handleMenuOpen = (event, row) => {
        setAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRow(null);
    };

    const handleStatusUpdateDialog = () => {
        setStatusUpdateOpen(!statusUpdateOpen);
    };

    const handleStatusUpdateOpen = (item) => {
        setSelectedCandidate(item);
        setStatusUpdateOpen(true);
    };

    const handleStatusUpdateClose = () => {
        setStatusUpdateOpen(false);
        setSelectedCandidate(null);
    };

    const extractFileNameFromUrl = (url) => {
        if (!url) return 'file';
        const parts = url.split('/');
        const lastPart = parts[parts.length - 1];
        return decodeURIComponent(lastPart.split('?')[0]);
    };

    const handleDownload = useCallback((fileUrl) => {
        const fileName = extractFileNameFromUrl(fileUrl);
        const fileExt = fileName.split('.').pop()?.toLowerCase();

        if (fileExt === 'pdf' || fileExt === 'doc' || fileExt === 'docx') {
            fetch(fileUrl)
                .then(r => r.blob())
                .then(blob => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                })
                .catch(error => {
                    console.error('Blob download failed:', error);
                    const a = document.createElement('a');
                    a.href = fileUrl;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                });
        } else {
            const a = document.createElement('a');
            a.href = fileUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }, []);

    useEffect(() => {
        getProfiles();
    }, [getProfiles]);

    const getRowNumber = (index) => {
        return (page - 1) * 10 + index + 1;
    };

    const navigateToPreviousPage = () => {
        navigate(-1);
    };

    // ✅ UPDATED: renderSelectedSuppliers uses user_id lookup
    const renderSelectedSuppliers = (selected) => {
        if (selected.length === 0) {
            return 'All Suppliers';
        }

        const selectedSuppliers = selected.map(id => {
            // ✅ CHANGED: Find by user_id instead of supplier_id
            const supplier = supplierData.find(s => s.user_id === parseInt(id));
            return supplier ? supplier.supplier_org_name : id;
        }).join(', ');

        return selectedSuppliers.length > 30
            ? `${selectedSuppliers.substring(0, 30)}...`
            : selectedSuppliers;
    };

    const parseStatusLabel = (status) => {
        if (!status) return "";
        return status
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };
    return (
        <Grid container className={classes.root}>
            <Grid item xs={12} className={classes.headerContainer}>
                <div style={{ marginTop: "20px" }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage}>
                        Back
                    </Button>
                </div>
            </Grid>

            <Grid item xs={12} className={classes.headerContainer}>
                <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                    <Grid item>
                        <h2 className={classes.pageTitle}>Candidates</h2>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={0} sx={{ alignItems: 'flex-end', mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="supplier-label" shrink>Select Suppliers</InputLabel>
                        <Select
                            labelId="supplier-label"
                            multiple
                            value={selectedUserIds}  // ✅ user_ids
                            label="Select Suppliers"
                            onChange={handleSupplierChange}
                            displayEmpty
                            renderValue={renderSelectedSuppliers}
                        >
                            <MenuItem value="all">
                                All Suppliers
                            </MenuItem>
                            {supplierData.map((supplier) => (
                                <MenuItem
                                    key={supplier?.user_id}      // ✅ Use user_id as key
                                    value={supplier?.user_id}    // ✅ value = user_id (61, 28)
                                >
                                    {supplier?.supplier_org_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            size="small"
                        >
                            Submit
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearFilters}
                        >
                            Clear
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {/* Table remains exactly the same */}
            <Grid item xs={12} className={classes.tableGrid}>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow className={classes.tableHeadRow}>
                                <TableCell className={classes.tableHeadCell}>Sr.no</TableCell>
                                <TableCell className={classes.tableHeadCell}>Candidate Name</TableCell>
                                <TableCell className={classes.tableHeadCell}>Resume</TableCell>
                                <TableCell className={classes.tableHeadCell}>Customer</TableCell>
                                <TableCell className={classes.tableHeadCell}>Supplier</TableCell>
                                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                                <TableCell className={classes.tableHeadCell}>Status Update Date</TableCell>
                                <TableCell align="right" className={classes.tableHeadCell}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className={classes.noHorizontalBorder}>
                            {candidates.map((row, index) => {
                                const rowId = row.profile_id || row.profileid;
                                const rowNumber = getRowNumber(index);

                                return (
                                    <TableRow key={rowId} hover>
                                        <TableCell className={classes.tableBodyCell} sx={{ fontWeight: 'bold', width: 50 }}>
                                            {rowNumber}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>{row.candidate_name}</TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {extractFileNameFromUrl(row.url)}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>{customer}</TableCell>
                                        <TableCell className={classes.tableBodyCell}>{row.supplier_org_name}</TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            <Tooltip
                                                title={parseStatusLabel(row?.detailed_status) || 'No detailed status'}
                                                arrow
                                                placement="top"
                                            >
                                                <Chip label={parseStatusLabel(row?.detailed_status)} size="small" color="default" />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.status_date ? format(new Date(row.status_date), "dd-MM-yyyy") : "-"}
                                        </TableCell>
                                        <TableCell align="left">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, row)}
                                                sx={{ color: "#2777AA" }}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {candidates.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No candidates found for applied filters.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <Pagination
                        classes={{ root: classes.pagination }}
                        count={Math.ceil(listCount / 10)}
                        page={page}
                        onChange={handlePageChange}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            </Grid>

            {/* Rest unchanged - Menu, Modal, Notification */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                <MenuItem
                    onClick={() => {
                        navigate("/admindashboard/viewFile", { state: { fileUrl: menuRow?.url } });
                        handleMenuClose();
                    }}
                >
                    <VisibilityIcon sx={{ mr: 1, color: "#2777AA" }} />
                    View
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleDownload(menuRow?.url);
                        handleMenuClose();
                    }}
                >
                    <DownloadIcon sx={{ mr: 1, color: "#2777AA" }} />
                    Download
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleStatusUpdateOpen(menuRow);
                        handleMenuClose();
                    }}
                >
                    <EditIcon sx={{ mr: 1, color: "#2777AA" }} />
                    Update
                </MenuItem>
            </Menu>

            <CustomModel
                handleDialog={handleStatusUpdateDialog}
                open={statusUpdateOpen}
                title="Status Update"
            >
                <AdminStatusUpdateForm
                    setAlert={setAlert}
                    setAlertType={setAlertType}
                    setAlertContent={setAlertContent}
                    item={selectedCandidate}
                    jobCode={jobCode}
                    handleDialog={handleStatusUpdateClose}
                    setUpdate={setUpdate}
                    update={update}
                />
            </CustomModel>

            <Notification
                message={alertContent}
                open={alert}
                severity={alertType}
                onClose={handleAlert}
            />
        </Grid>
    );
};

export default SupplierCandidateList;
